import { ActionReference, GameObjectReference, GameState } from "@shared/types";
import { LitElement, TemplateResult, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { getState, performAction } from "../services/routeService";
import GameCanvasCss from "./GameCanvasCss";

//import { url } from "inspector";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = GameCanvasCss;

    @state() private currentText: string = "";
    @state() private isTyping: boolean = false;

    @property({type: Array}) private contentText: string[] = [];
    private contentIndex: number = 0;
    private letterIndex: number = 0;

    private roomTitle?: string;
    private roomImages?: string[];
    // @Property({type : string})
    @state()
    // private inputText?: string = "Default Value";
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    private updateState(state: GameState): void {
        
        //Reset the component
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;
        void this.typeWriter();

        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        this.requestUpdate();
    }

    private typeWriter(): void {
    const newContentTextString: string = JSON.stringify(this.contentText);
    const lastContentTextString: string | null = localStorage.getItem("lastContent");

    if (!this.isTyping) {
        if (newContentTextString !== lastContentTextString) {
            localStorage.setItem("lastContent", JSON.stringify(this.contentText));
            this.contentIndex = 0;
            this.letterIndex = 0;
            this.currentText = "";
            this.isTyping = true;

            // Start typing the first piece of content
            this.typeNextLetter();
        } else {
            this.currentText = this.contentText.join("\n\n");
        }
    }
    }

    private typeNextLetter(): void {
        if (this.contentIndex < this.contentText.length) {
            const currentPiece: string = this.contentText[this.contentIndex];

            if (this.letterIndex < currentPiece.length) {
                // Typing out the current letter
                this.currentText += currentPiece.charAt(this.letterIndex++);
                setTimeout(() => this.typeNextLetter(), 5);
            } else {
                // Check if there's another piece of text to type out
                if (this.contentIndex < this.contentText.length - 1) {
                    // If not the last item, add a line break for the next item
                    this.currentText += "\n\n"; // Adds a line break
                }
                // Move to the next piece of text, or finish if there was no new line added
                this.letterIndex = 0;
                this.contentIndex++;
                setTimeout(() => this.typeNextLetter(), 200); // Delay before starting the next piece or finishing
            }
        } else {
            // Typing finished for all pieces, marking typing as complete
            this.isTyping = false;
        }
    }


    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias)
        );

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    protected render(): TemplateResult {
        return html`
            <div class="game">
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderContent()} ${this.renderFooter()}
            </div>
        `;
    }

    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`<div class="title">${this.roomTitle}</div>`;
        }

        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            console.log( this.roomImages?.map((url) => html`<img src="/assets/img/${url}.png" />`));
            if (this.roomImages && this.roomImages.length > 2){
                return html`
                <div>
                ${this.roomImages?.map((url) => html`<img class="${url}" src="/assets/img/${url}.png" />`)}
                </div>
                `;
            }

            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`<img src="/assets/img/${url}.png" />`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    public log(evt: Event): void {
        const target: HTMLInputElement = evt.target as HTMLInputElement;
        const { value } = target;

        console.log(value);
    }

        // Hierin moet de <input> komen te staan
        // Kan het niet doen op basis van playersession omdat dit in de web gedeelte is en playersession in de api gedeelte is.
    private renderContent(): TemplateResult {
        return html`
            <div class="content">
                <p class="content-text">${this.currentText}</p>
            </div>`;
    }

    //     return html`<div class="content">${this.contentText?.map((text) => html`<p>${text}</p>`)}</div>
    //         <div class="text_input">
    //             <input class="input_field" @input=${this.log} />
    //             <button class="input_button">Confirm</button>
    //         </div>`;
    // }

    private renderFooter(): TemplateResult {
        return html`
                <div class="footer">
                    <div class="buttons">
                        <div>
                        ${this.actionButtons?.map((button) => html`
                            <a class="button ${this.selectedActionButton?.alias === button.alias ? "active" : ""}"
                                tabindex="0"
                                @click=${async (): Promise<void> => {
                                    if (!this.isTyping) await this.handleClickAction(button);
                                }}
                                @keydown=${async (e: KeyboardEvent): Promise<void> => {
                                    if (e.key === "Enter" && !this.isTyping) await this.handleClickAction(button);
                                }}
                            >${button.label}</a>
                        `)}
                    </div>
                    <div>
                        ${this.selectedActionButton
                            ? this.gameObjectButtons?.map(
                                  (button) => html`<a
                                      class="button ${this.selectedGameObjectButtons.has(button)
                                          ? "active"
                                          : ""}"
                                      @click=${(): void => void this.handleClickObject(button)}
                                      >${button.name}</a
                                  >`
                              )
                            : nothing}
                    </div>
                </div>
            </div>
        `;
    }}
    

 












